import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Custom MyLeaf-themed marker — dark olive pin with gold ring.
const PIN_ICON = L.divIcon({
  className: 'myleaf-grave-pin',
  html:
    '<div style="width:26px;height:34px;display:flex;align-items:flex-start;justify-content:center;">' +
    '<svg viewBox="0 0 24 32" width="26" height="34" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M12 0 C5 0 0 5 0 12 C0 22 12 32 12 32 C12 32 24 22 24 12 C24 5 19 0 12 0 Z" fill="#1A3626" stroke="#D9B775" stroke-width="2"/>' +
    '<circle cx="12" cy="12" r="4" fill="#D9B775"/>' +
    '</svg></div>',
  iconSize: [26, 34],
  iconAnchor: [13, 32],
});

function ClickToPlace({ onPlace }) {
  useMapEvents({
    click(e) {
      onPlace(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function CenterOnLoad({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, zoom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center?.[0], center?.[1]]);
  return null;
}

/**
 * GravesitePinPicker
 * ------------------
 * Admin-only interactive map. Click anywhere on the map to drop a gravesite
 * pin. The "Use my location" button asks the browser for geolocation so the
 * admin can drop the pin from the cemetery itself.
 *
 * Renders no map tile API key — uses free OpenStreetMap tiles.
 */
export function GravesitePinPicker({ lat, lng, onChange, geocodedCenter, height = 320 }) {
  const initial = lat != null && lng != null
    ? [Number(lat), Number(lng)]
    : (geocodedCenter || [39.5, -98.35]); // continental US center fallback
  const [center, setCenter] = useState(initial);
  const [zoom, setZoom] = useState(lat != null && lng != null ? 18 : 4);
  const [locating, setLocating] = useState(false);
  const mapRef = useRef(null);

  useEffect(() => {
    if (lat != null && lng != null) {
      setCenter([Number(lat), Number(lng)]);
      setZoom(18);
    } else if (geocodedCenter) {
      setCenter(geocodedCenter);
      setZoom((z) => (z < 13 ? 13 : z));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng, geocodedCenter?.[0], geocodedCenter?.[1]]);

  const useMyLocation = () => {
    if (!('geolocation' in navigator)) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        onChange?.(latitude, longitude);
        setCenter([latitude, longitude]);
        setZoom(19);
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 12000 },
    );
  };

  return (
    <div data-testid="gravesite-pin-picker">
      <div
        className="rounded-md overflow-hidden border border-[hsl(var(--primary)/0.2)]"
        style={{ height }}
      >
        <MapContainer
          center={center}
          zoom={zoom}
          ref={mapRef}
          scrollWheelZoom
          style={{ width: '100%', height: '100%' }}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <CenterOnLoad center={center} zoom={zoom} />
          <ClickToPlace onPlace={(la, ln) => onChange?.(la, ln)} />
          {lat != null && lng != null && (
            <Marker
              position={[Number(lat), Number(lng)]}
              icon={PIN_ICON}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const { lat: la, lng: ln } = e.target.getLatLng();
                  onChange?.(la, ln);
                },
              }}
            />
          )}
        </MapContainer>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3 text-[12px] text-[hsl(var(--primary))]/80">
        <button
          type="button"
          onClick={useMyLocation}
          disabled={locating}
          className="ml-btn-outline !py-2 text-xs"
          data-testid="gravesite-use-my-location-btn"
        >
          {locating ? 'Locating…' : 'Use my current location'}
        </button>
        {lat != null && lng != null ? (
          <span data-testid="gravesite-coords-readout" className="font-mono">
            Pin · {Number(lat).toFixed(6)}, {Number(lng).toFixed(6)}
          </span>
        ) : (
          <span className="italic">Tap or click anywhere on the map to drop the pin.</span>
        )}
        {lat != null && lng != null && (
          <button
            type="button"
            onClick={() => onChange?.(null, null)}
            className="text-[11px] tracking-[0.18em] uppercase font-bold text-[hsl(var(--destructive))] hover:underline"
            data-testid="gravesite-clear-pin-btn"
          >
            Clear pin
          </button>
        )}
      </div>
    </div>
  );
}
